// @flow
import { getAll, getCurrent, getNavigation } from '@rover/react-lib/apis/countryConfiguration.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/lib/redux-duck';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type { CountryConfiguration } from '@rover/react-lib/types';
import partialRegExp from '@rover/utilities/partialRegExp';
import reportError from '@rover/utilities/reportError';

export type State = {
  countryConfigurations: Array<CountryConfiguration>,
  currentCountryConfiguration: ?CountryConfiguration,
  navigationCountryConfiguration: ?CountryConfiguration,
  errorMessage: string,
  isLoading: boolean,
};

type Actions = {
  fetchConfigurationPending: ActionCreator<void>,
  fetchConfigurationFailure: ActionCreator<Error>,
  fetchConfigurationSuccess: ActionCreator<{
    currentCountryConfiguration: CountryConfiguration,
    countryConfigurations: Array<CountryConfiguration>,
    navigationCountryConfiguration: CountryConfiguration,
  }>,
};

type Effects = {
  fetchConfiguration: EffectCreator<void>,
};

type Selectors = {
  getIsLoading: SelectorCreator<boolean>,
  getErrorMessage: SelectorCreator<?string>,

  getCurrentCountryConfiguration: SelectorCreator<?CountryConfiguration>,
  getAvailableLanguages: SelectorCreator<?Array<[string, string]>>,

  getCurrentPostalCodeRegex: SelectorCreator<?RegExp>,
  getCurrentPartialPostalCodeRegex: SelectorCreator<?RegExp>,
  getCurrentIsStateRequired: SelectorCreator<?boolean>,
  getCurrentCustomerSupportPhoneFormatted: SelectorCreator<?string>,
  getCurrentEmergencyHotlinePhoneFormatted: SelectorCreator<?string>,

  getAllCountryConfigurations: SelectorCreator<Array<CountryConfiguration>>,
  getAllFormattedRegexes: SelectorCreator<Array<RegExp>>,
  getCombinedRegex: SelectorCreator<RegExp>,
  getCombinedPartialRegex: SelectorCreator<RegExp>,

  getCountryConfigurationByPostalCode: SelectorCreator<(string) => ?CountryConfiguration>,
  getIsStateRequiredByPostalCode: SelectorCreator<(string) => boolean>,
  getStateFieldNameByPostalCode: SelectorCreator<(string) => string>,
  getWeightLabel: SelectorCreator<string>,
  getCurrentCountryIndexUrl: SelectorCreator<string>,
  getCurrentContinentIsEurope: SelectorCreator<boolean>,
  getCurrentCountryCode: SelectorCreator<string>,
  getCurrentCurrencyCode: SelectorCreator<string>,
  getCurrentCountryUrls: SelectorCreator<{ [string]: string }>,
  getCurrentCountryMinimumAddOnPrices: SelectorCreator<{ [string]: { [string]: number } }>,

  getCountryCodes: SelectorCreator<Array<string>>,
  getExtenuatingCircumstancesArticleURL: SelectorCreator<string>,
  getEmergencyHotlineHours: SelectorCreator<string>,

  getNavigationCountryConfiguration: SelectorCreator<?CountryConfiguration>,
  getNavigationAvailableLanguages: SelectorCreator<?Array<[string, string]>>,
  getNavigationCurrentContinentIsEurope: SelectorCreator<boolean>,
  getNavigationCurrentCountryIsUS: SelectorCreator<boolean>,
  getNavigationCurrencyCode: SelectorCreator<string>,

  getPriceFilterMaxValue: SelectorCreator<?number>,
  getPriceFilterMinValue: SelectorCreator<?number>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

const formatRegex = inputRegex => {
  /**
   * Some postal code regexes come from the backend
   * without the needed leading carrot (^). This ensures
   * that the regex starts with a carrot.
   */
  const regEx = inputRegex[0] === '^' ? inputRegex : `^${inputRegex}`;

  return new RegExp(regEx);
};

export const options: Options = {
  name: 'countryConfiguration',
  initialState: {
    isLoading: false,
    errorMessage: '',
    currentCountryConfiguration: null,
    countryConfigurations: [],
    navigationCountryConfiguration: null,
  },
  actions: {
    fetchConfigurationPending: () => state => ({
      ...state,
      errorMessage: '',
      isLoading: true,
    }),
    fetchConfigurationFailure: ({ message }) => state => ({
      ...state,
      errorMessage: message,
      isLoading: false,
    }),
    fetchConfigurationSuccess: ({
      currentCountryConfiguration,
      countryConfigurations,
      navigationCountryConfiguration,
    }) => state => ({
      ...state,
      currentCountryConfiguration,
      countryConfigurations,
      navigationCountryConfiguration,
      errorMessage: '',
      isLoading: false,
    }),
  },
  effects: {
    fetchConfiguration: () => async (
      dispatch,
      getState,
      { fetchConfigurationPending, fetchConfigurationSuccess, fetchConfigurationFailure },
      duckEffects,
      duckSelectors,
      { expressParams }
    ) => {
      dispatch(fetchConfigurationPending());

      try {
        const [
          currentCountryConfiguration,
          countryConfigurations,
          navigationCountryConfiguration,
        ] = await Promise.all([
          getCurrent(expressParams),
          getAll(expressParams),
          getNavigation(expressParams),
        ]);

        dispatch(
          fetchConfigurationSuccess({
            currentCountryConfiguration,
            countryConfigurations,
            navigationCountryConfiguration,
          })
        );
      } catch (e) {
        dispatch(fetchConfigurationFailure(e));
        reportError(e);
      }
    },
  },
  selectors: {
    getIsLoading: (getDuckState, createSelector) =>
      createSelector([getDuckState], state => state.isLoading),
    getErrorMessage: (getDuckState, createSelector) =>
      createSelector([getDuckState], state => state.errorMessage),
    getCurrentCountryConfiguration: (getDuckState, createSelector) =>
      createSelector([getDuckState], state => state.currentCountryConfiguration),
    getAvailableLanguages: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration => {
        if (!countryConfiguration) return null;
        return countryConfiguration.availableLanguages;
      }),
    getCurrentPostalCodeRegex: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration => {
        if (!countryConfiguration) return null;
        return formatRegex(countryConfiguration.postalCodeRegex);
      }),
    getCurrentPartialPostalCodeRegex: (
      getDuckState,
      createSelector,
      { getCurrentPostalCodeRegex }
    ) => createSelector([getCurrentPostalCodeRegex], regex => regex && partialRegExp(regex)),
    getCurrentIsStateRequired: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector(
        [getCurrentCountryConfiguration],
        countryConfiguration => countryConfiguration && countryConfiguration.isStateRequired
      ),
    getCurrentCustomerSupportPhoneFormatted: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        countryConfiguration =>
          countryConfiguration &&
          countryConfiguration.phoneNumbers &&
          countryConfiguration.phoneNumbers.customerSupportPhoneFormatted
      ),
    getCurrentEmergencyHotlinePhoneFormatted: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector(
        [getCurrentCountryConfiguration],
        countryConfiguration =>
          countryConfiguration &&
          countryConfiguration.phoneNumbers &&
          countryConfiguration.phoneNumbers.emergencyHotlinePhoneFormatted
      ),

    getAllCountryConfigurations: (getDuckState, createSelector) =>
      createSelector([getDuckState], state => state.countryConfigurations),
    getAllFormattedRegexes: (getDuckState, createSelector, { getAllCountryConfigurations }) =>
      createSelector([getAllCountryConfigurations], countryConfigurations =>
        countryConfigurations.map(config => formatRegex(config.postalCodeRegex))
      ),
    getCombinedRegex: (getDuckState, createSelector, { getAllFormattedRegexes }) =>
      createSelector(
        [getAllFormattedRegexes],
        regexes => new RegExp(regexes.map(r => r.source).join('|'))
      ),
    getCombinedPartialRegex: (getDuckState, createSelector, { getCombinedRegex }) =>
      createSelector([getCombinedRegex], regex => regex && partialRegExp(regex)),

    getCountryConfigurationByPostalCode: (
      getDuckState,
      createSelector,
      {
        getAllCountryConfigurations,
        getCurrentCountryConfiguration,
        getCurrentPartialPostalCodeRegex,
      }
    ) =>
      createSelector(
        [
          getAllCountryConfigurations,
          getCurrentCountryConfiguration,
          getCurrentPartialPostalCodeRegex,
        ],
        (countryConfigurations, currentCountryConfiguration, partial) => postalCode => {
          if (!postalCode || (partial && partial.test(postalCode))) {
            return currentCountryConfiguration;
          }
          return (
            countryConfigurations.find(config =>
              partialRegExp(formatRegex(config.postalCodeRegex)).test(postalCode)
            ) || currentCountryConfiguration
          );
        }
      ),
    getIsStateRequiredByPostalCode: (
      getDuckState,
      createSelector,
      { getCountryConfigurationByPostalCode }
    ) =>
      createSelector(
        [getCountryConfigurationByPostalCode],
        countryConfigurationByPostalCode => postalCode => {
          const config = countryConfigurationByPostalCode(postalCode);
          return config ? config.isStateRequired : true;
        }
      ),
    getCurrentContinentIsEurope: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration => {
        if (!countryConfiguration) return false;
        return countryConfiguration.continentCode === 'EU';
      }),
    getCurrentCountryCode: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration =>
        countryConfiguration ? countryConfiguration.countryCode : 'US'
      ),
    getCurrentCurrencyCode: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration =>
        countryConfiguration ? countryConfiguration.currencyCode : 'USD'
      ),

    getCurrentCountryUrls: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], currentCountryConfiguration => {
        if (!currentCountryConfiguration) return {};
        return currentCountryConfiguration.urls;
      }),

    getCurrentCountryMinimumAddOnPrices: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], currentCountryConfiguration => {
        if (!currentCountryConfiguration) return {};
        return currentCountryConfiguration.minimumServiceAddOnPrices;
      }),

    getCurrentCountryIndexUrl: (getDuckState, createSelector, { getCurrentCountryUrls }) =>
      createSelector([getCurrentCountryUrls], currentCountryUrls => {
        if (currentCountryUrls === {}) return 'http://www.rover.com/';
        return currentCountryUrls.indexUrl;
      }),

    getCountryCodes: (getDuckState, createSelector, { getAllCountryConfigurations }) =>
      createSelector([getAllCountryConfigurations], allCountryConfigurations =>
        allCountryConfigurations.map(countryConfiguration => countryConfiguration.countryCode)
      ),

    getStateFieldNameByPostalCode: (
      getDuckState,
      createSelector,
      { getCountryConfigurationByPostalCode }
    ) =>
      createSelector(
        [getCountryConfigurationByPostalCode],
        countryConfigurationByPostalCode => postalCode => {
          const config = countryConfigurationByPostalCode(postalCode);
          return config && config.countryCode === 'CA' ? 'Province' : 'State';
        }
      ),

    getWeightLabel: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration => {
        const weightLabel = countryConfiguration ? countryConfiguration.weightUnit : 'lbs';
        return weightLabel;
      }),

    getExtenuatingCircumstancesArticleURL: (
      getDuckState,
      createSelector,
      { getCurrentCountryConfiguration }
    ) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration => {
        const articleURL = countryConfiguration
          ? countryConfiguration.urls.extenuatingCircumstancesHelpdeskUrl
          : 'https://support.rover.com/hc/en-us/articles/360031574231';
        return articleURL;
      }),

    getEmergencyHotlineHours: (getDuckState, createSelector, { getCurrentCountryConfiguration }) =>
      createSelector([getCurrentCountryConfiguration], countryConfiguration =>
        countryConfiguration ? countryConfiguration.emergencyHotlineHours : ''
      ),

    // If you navigate to a new country-specific page, this still returns the previous country
    // value. It may be a cookie issue.
    // See DEV-41869
    getNavigationCountryConfiguration: (getDuckState, createSelector) =>
      createSelector([getDuckState], state => state.navigationCountryConfiguration),
    getNavigationAvailableLanguages: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], navigationConfiguration => {
        if (!navigationConfiguration) return null;
        return navigationConfiguration.availableLanguages;
      }),
    getNavigationCurrentContinentIsEurope: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], navigationConfiguration => {
        if (!navigationConfiguration) return false;
        return navigationConfiguration.continentCode === 'EU';
      }),
    getNavigationCurrentCountryIsUS: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], navigationConfiguration => {
        if (!navigationConfiguration) return true;
        return navigationConfiguration.countryCode === 'US';
      }),
    getNavigationCurrencyCode: (
      getDuckState,
      createSelector,
      { getNavigationCountryConfiguration }
    ) =>
      createSelector([getNavigationCountryConfiguration], countryConfiguration =>
        countryConfiguration ? countryConfiguration.currencyCode : 'EUR'
      ),
    getPriceFilterMaxValue: (getDuckState, createSelector, { getNavigationCountryConfiguration }) =>
      createSelector(
        [getNavigationCountryConfiguration],
        countryConfiguration => countryConfiguration && countryConfiguration.priceFilterMaxValue
      ),
    getPriceFilterMinValue: (getDuckState, createSelector, { getNavigationCountryConfiguration }) =>
      createSelector(
        [getNavigationCountryConfiguration],
        countryConfiguration => countryConfiguration && countryConfiguration.priceFilterMinValue
      ),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/redux/ducks/countryConfiguration.duck.js